import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Mail, MapPin, Globe, Check, Trash2, MessageCircle, Clock, RotateCcw, Paperclip } from "lucide-react";
import { toast } from "sonner";

type Feedback = {
  id: string;
  category: "bug" | "update" | "idea" | "other";
  subcategory: string | null;
  message: string;
  email: string | null;
  shelter_name: string | null;
  page_url: string | null;
  user_agent: string | null;
  viewport: string | null;
  status: "new" | "read" | "done";
  created_at: string;
  attachment_path: string | null;
};

const CATEGORY_LABEL: Record<Feedback["category"], { label: string; emoji: string }> = {
  bug: { label: "Błąd", emoji: "🐛" },
  update: { label: "Aktualizacja", emoji: "📝" },
  idea: { label: "Pomysł", emoji: "💡" },
  other: { label: "Inne", emoji: "🤝" },
};

export const FeedbackTab = () => {
  const [items, setItems] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "new" | "read" | "done">("new");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("feedback_messages")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error("Nie udało się pobrać wiadomości");
    } else {
      setItems((data ?? []) as Feedback[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id: string, status: Feedback["status"]) => {
    const { data, error } = await supabase
      .from("feedback_messages")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select("id");
    if (error) {
      console.error("Feedback update error:", error);
      toast.error(`Błąd aktualizacji: ${error.message}`);
      return;
    }
    if (!data || data.length === 0) {
      toast.error("Brak uprawnień lub wiadomość nie istnieje");
      return;
    }
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    toast.success(
      status === "read" ? "Oznaczono: w trakcie" : status === "done" ? "Oznaczono: rozwiązane" : "Przywrócono",
    );
  };

  const remove = async (id: string) => {
    if (!confirm("Usunąć tę wiadomość na stałe?")) return;
    const { error } = await supabase.from("feedback_messages").delete().eq("id", id);
    if (error) {
      toast.error("Błąd usuwania");
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success("Usunięto");
  };

  const filtered = items.filter((i) => filter === "all" || i.status === filter);
  const newCount = items.filter((i) => i.status === "new").length;
  const inProgressCount = items.filter((i) => i.status === "read").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          Wiadomości od użytkowników
        </h2>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="new">
              Nowe
              {newCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {newCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="read">
              W trakcie
              {inProgressCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {inProgressCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="done">Rozwiązane</TabsTrigger>
            <TabsTrigger value="all">Wszystkie</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            Brak wiadomości w tej kategorii.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const meta = CATEGORY_LABEL[it.category];
            return (
              <Card
                key={it.id}
                className={`border-l-4 ${
                  it.status === "new"
                    ? "border-l-primary"
                    : it.status === "read"
                    ? "border-l-amber-500"
                    : "border-l-muted-foreground/30"
                }`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {meta.emoji} {meta.label}
                      </Badge>
                      {it.subcategory && (
                        <Badge variant="outline" className="text-xs font-normal">
                          {it.subcategory}
                        </Badge>
                      )}
                      {it.status === "new" && (
                        <Badge className="text-xs bg-primary">Nowe</Badge>
                      )}
                      {it.status === "read" && (
                        <Badge className="text-xs bg-amber-500 hover:bg-amber-500">
                          <Clock className="w-3 h-3 mr-1" />
                          W trakcie
                        </Badge>
                      )}
                      {it.status === "done" && (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          ✓ Rozwiązane
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(it.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                    </span>
                  </div>

                  {it.shelter_name && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="font-medium">{it.shelter_name}</span>
                    </div>
                  )}

                  <div className="bg-muted/40 rounded-lg p-3 text-sm whitespace-pre-wrap">
                    {it.message}
                  </div>

                  {it.attachment_path && (
                    <FeedbackAttachmentLink path={it.attachment_path} />
                  )}

                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-muted-foreground">
                    <div className="flex items-center gap-3 flex-wrap">
                      {it.email ? (
                        <a
                          href={`mailto:${it.email}?subject=Re: GdzieSchronisko`}
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          {it.email}
                        </a>
                      ) : (
                        <span className="flex items-center gap-1 text-destructive">
                          <Mail className="w-3.5 h-3.5" />
                          brak maila
                        </span>
                      )}
                      {it.page_url && (
                        <a
                          href={it.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-foreground truncate max-w-[200px]"
                          title={it.page_url}
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {it.page_url.replace(/^https?:\/\//, "").slice(0, 40)}
                        </a>
                      )}
                      {it.viewport && <span>📱 {it.viewport}</span>}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1 flex-wrap">
                    {it.status === "new" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(it.id, "read")}
                        className="flex-1"
                      >
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        W trakcie
                      </Button>
                    )}
                    {it.status !== "done" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(it.id, "done")}
                        className="flex-1"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Rozwiązane
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(it.id, "new")}
                        className="flex-1"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Cofnij do nowych
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(it.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FeedbackAttachmentLink = ({ path }: { path: string }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase.storage
      .from("feedback-attachments")
      .createSignedUrl(path, 60 * 60)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[FeedbackAttachmentLink] signed url error", error);
        } else {
          setUrl(data?.signedUrl ?? null);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const isImage = /\.(jpe?g|png|webp|gif)$/i.test(path);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Paperclip className="w-3.5 h-3.5" />
        <span>Załącznik</span>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Ładowanie podglądu...
        </div>
      ) : url ? (
        isImage ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={url}
              alt="Załącznik zgłoszenia"
              className="max-h-64 rounded-md border border-border object-contain bg-background"
            />
          </a>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline break-all"
          >
            {path}
          </a>
        )
      ) : (
        <span className="text-xs text-destructive">Nie udało się wczytać załącznika</span>
      )}
    </div>
  );
};
